/**
 * Component: EventSessionsTab
 * Displays and manages event sessions
 */

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Calendar, Clock } from 'lucide-react';
import type { EventSession } from '@/types/event.types';
import {
  formatDate,
  formatSessionTimeRange,
  groupSessionsByDate,
} from '@/models/event';
import { InlineLoader } from '@/components/ui/pulsing-loader';

interface EventSessionsTabProps {
  sessions: EventSession[];
  isLoading: boolean;
  canEdit: boolean;
  onAddSession: () => void;
  onEditSession: (session: EventSession) => void;
  onDeleteSession: (session: EventSession) => void;
}

export function EventSessionsTab({
  sessions,
  isLoading,
  canEdit,
  onAddSession,
  onEditSession,
  onDeleteSession,
}: EventSessionsTabProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <InlineLoader message="Cargando sesiones..." />
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Calendar className="mx-auto mb-4 size-12 text-muted-foreground" />
          <p className="mb-4 text-muted-foreground">
            No hay sesiones configuradas para este evento
          </p>
          {canEdit && (
            <Button onClick={onAddSession}>
              <Plus className="mr-2 size-4" />
              Agregar primera sesión
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  // Group sessions by date
  const groupedSessions = groupSessionsByDate(sessions);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Programa del evento</h2>
          <p className="text-sm text-muted-foreground">
            {sessions.length}{' '}
            {sessions.length === 1
              ? 'sesión configurada'
              : 'sesiones configuradas'}
          </p>
        </div>
        {canEdit && (
          <Button onClick={onAddSession}>
            <Plus className="mr-2 size-4" />
            Agregar sesión
          </Button>
        )}
      </div>

      {/* Sessions grouped by date */}
      {Array.from(groupedSessions.entries()).map(([date, dateSessions]) => (
        <Card key={date}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calendar className="size-5" />
              {formatDate(date)}
            </CardTitle>
            <CardDescription>
              {dateSessions.length}{' '}
              {dateSessions.length === 1 ? 'sesión' : 'sesiones'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dateSessions.map((session) => (
                <div
                  key={session.id}
                  className="flex items-start justify-between rounded-lg border p-4 transition-colors hover:bg-muted/50"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">{session.name}</h3>
                    </div>
                    {session.description && (
                      <p className="mt-1 text-sm text-muted-foreground">
                        {session.description}
                      </p>
                    )}
                    <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="size-4" />
                      {formatSessionTimeRange(session) ||
                        'Hora no especificada'}
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEditSession(session)}
                      >
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDeleteSession(session)}
                        className="text-destructive hover:text-destructive"
                      >
                        Eliminar
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
